// Utilities for lightweight image URL optimization without a backend

/**
 * Optimize third-party avatar URLs to an approximate target square size.
 * - Google avatars: replace wXX-hXX with requested size
 * - Facebook avatars: prefer `s{size}x{size}` if present, otherwise add `&width`/`&height`
 */
export function optimizeAvatarUrl(url, size = 48) {
  try {
    if (!url) return url;
    let next = String(url).trim();

    // Helper to proxy an arbitrary external image through images.weserv.nl
    const proxyViaWeserv = (u) => {
      const withoutProto = String(u).replace(/^https?:\/\//, '');
      return `https://images.weserv.nl/?url=${encodeURIComponent(withoutProto)}&w=${size}&h=${size}&fit=cover`;
    };

    // Google avatars (lh3.googleusercontent.com) - keep using Google's sizing tokens where possible
    if (next.includes('lh3.googleusercontent.com')) {
      // Replace wXX-hXX tokens
      next = next.replace(/w\d+-h\d+/g, `w${size}-h${size}`);
      // Some variants use '=sNN' or 'sNN-c'
      next = next.replace(/=s\d+(-c)?/, `=s${size}$1`);
      return next;
    }

    // Facebook and related CDNs often block hotlinking or require auth.
    // Proxy those through images.weserv.nl to avoid CORS/hotlink problems.
    if (next.includes('fbcdn') || next.includes('facebook.com') || next.includes('scontent-')) {
      return proxyViaWeserv(next);
    }

    // Otherwise, return the original URL unchanged.
    return next;
  } catch {
    return url;
  }
}

/**
 * Clamp local logo sizes for <img width/height> props.
 */
export function clampSize(width, height, max = 512) {
  const w = Math.min(Number(width) || max, max);
  const h = Math.min(Number(height) || max, max);
  return { width: w, height: h };
}

const DEFAULT_ORIGIN = 'https://br.lk';
const WESERV_HOST = 'https://images.weserv.nl/?url=';

function ensureAbsoluteUrl(pathOrUrl, fallbackOrigin = DEFAULT_ORIGIN) {
  if (!pathOrUrl) return undefined;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : fallbackOrigin;
  return `${origin}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
}

function sanitizeForWeserv(url) {
  if (!url) return undefined;
  if (url.startsWith('https://')) return `ssl:${url.slice('https://'.length)}`;
  return url.replace(/^http:\/\//i, '');
}

export function buildResponsiveSrcSet(assetPath, widths = [320, 480, 768, 1024], options = {}) {
  const absolute = ensureAbsoluteUrl(assetPath);
  if (!absolute) return undefined;
  try {
    const parsed = new URL(absolute);
    const host = parsed.hostname;
    if (!host || host === 'localhost' || host.startsWith('127.') || host.endsWith('.local')) {
      return undefined;
    }
    if (parsed.protocol !== 'https:') {
      return undefined;
    }
  } catch {
    return undefined;
  }
  const { format = 'webp', quality = 75, dpr, sharpen } = options;
  const sanitized = sanitizeForWeserv(absolute);
  const unique = [...new Set(widths.filter((w) => Number.isFinite(w) && w > 0))].sort((a, b) => a - b);
  if (!unique.length) return undefined;
  const base = `${WESERV_HOST}${encodeURIComponent(sanitized)}`;
  return unique
    .map((w) => {
      const params = new URLSearchParams();
      params.set('w', String(w));
      if (format) params.set('output', format);
      if (quality) params.set('q', String(quality));
      params.set('we', '1');
      params.set('il', '1');
      if (dpr) params.set('dpr', String(dpr));
      if (sharpen) params.set('sharp', '1');
      const query = params.toString();
      return `${base}&${query} ${w}w`;
    })
    .join(', ');
}

export function defaultSizes(fallback = '100vw') {
  return fallback;
}
